/**
 * The entity registry: every unit and structure in both factions, as data
 * (R13, KTD5).
 *
 * The roster is deliberately bounded at **6 units and 8 structures per
 * faction**. It is the largest cost multiplier in the plan — every entity costs
 * a registry record, a mesh recipe, a distinguishable silhouette, an icon, and
 * a balance-sweep obligation — and because content is data, entities beyond the
 * minimum are additive work that lands safely *after* the balance sweep passes
 * rather than scope carried through the whole build.
 *
 * The two rosters mirror in role and differ in stats and behaviour, which is
 * Red Alert's actual structure and the cheapest balance starting point.
 */

import * as fix from '../../core/fixed.js'
import { ARMOR } from './armor.js'
import { NO_WEAPON } from './weapons.js'

/** Sidebar tabs, matching Red Alert 2's four. */
export const TAB = {
  Structure: 0,
  Defense: 1,
  Infantry: 2,
  Vehicle: 3,
} as const

export type Tab = (typeof TAB)[keyof typeof TAB]

export const TAB_NAMES = ['structures', 'defenses', 'infantry', 'vehicles'] as const

export const FACTION = {
  Helios: 0,
  Ascendancy: 1,
} as const

export type Faction = (typeof FACTION)[keyof typeof FACTION]

/** Roles, used to assert the two rosters mirror each other. */
export const ROLE = {
  BaseRoot: 'base-root',
  Power: 'power',
  Refinery: 'refinery',
  InfantryProduction: 'infantry-production',
  VehicleProduction: 'vehicle-production',
  TechLab: 'tech-lab',
  Airbase: 'airbase',
  Defense: 'defense',
  Superweapon: 'superweapon',
  Founder: 'founder',
  Harvester: 'harvester',
  AntiInfantry: 'anti-infantry',
  Support: 'support',
  Scout: 'scout',
  MainBattle: 'main-battle',
  Air: 'air',
  /** Tier-three lab. Gates the roles below it. */
  AdvancedLab: 'advanced-lab',
  /** Focused-light tank. Long reach, slow rate of fire, weak to infantry. */
  Beam: 'beam',
  /** Indirect fire. Outranges defences, cannot shoot close, must be escorted. */
  Artillery: 'artillery',
  /** Slow, heavily armoured brawler. Holds ground that a tank cannot. */
  Heavy: 'heavy',
  /** Outranges everything, kills only people. The answer to massed infantry. */
  Marksman: 'marksman',
  /** Carries one infantryman and fights with their weapon. */
  Transport: 'transport',
} as const

export type Role = (typeof ROLE)[keyof typeof ROLE]

export interface EntityDef {
  readonly id: number
  readonly key: string
  readonly name: string
  readonly faction: Faction
  readonly role: Role
  readonly tab: Tab
  readonly isStructure: boolean

  readonly cost: number
  /** Ticks at full power. 30 is one second. */
  readonly buildTime: number
  /** Entity ids that must exist and be alive to build this. */
  readonly prereqs: readonly number[]

  /** Positive produces power, negative consumes it. */
  readonly power: number
  readonly hp: number
  readonly armor: number
  readonly weapon: number
  /** Q16.16 cells per tick. Zero for structures. */
  readonly speed: fix.Fixed
  /** Whole cells. */
  readonly vision: number
  /** Footprint in cells. Units are always 1×1. */
  readonly width: number
  readonly height: number

  /** Can deploy into a structure, and keeps a player alive under R30. */
  readonly founderOf?: number
  readonly isHarvester?: boolean
  /**
   * An engineer: repairs a friendly structure to full by entering it (and is
   * consumed), and turns a transport it rides in into a repair vehicle.
   */
  readonly isEngineer?: boolean
  /** Regolith a harvester holds before returning. */
  readonly capacity?: number
  /** Structure that spawns units for this tab. */
  readonly producesTab?: Tab
  /** Superweapon charge time in ticks. */
  readonly chargeTime?: number
  /** Structures that arrive with a free unit — the Refinery's harvester (F1). */
  readonly freeUnit?: number

  /**
   * Lifts the shroud: the whole map becomes *explored* while this stands.
   *
   * Explored is not the same as visible, and the distinction is the whole
   * feature. Terrain and remembered structures appear, so the minimap stops
   * being a black square and an attack can be planned — but live enemy units
   * stay hidden unless something of yours can actually see them. Revealing
   * those would delete the scouting game rather than reward building for it.
   */
  readonly revealsMap?: boolean
  /**
   * Can dig in: press D to trade mobility for cover.
   *
   * Infantry only. A vehicle that could do this would simply be a defence
   * structure that walked to the spot it wanted to be a defence structure at,
   * which is a different unit and probably a worse one.
   */
  readonly canDigIn?: boolean
  /**
   * Carries one infantryman, and fights with whatever that passenger carries.
   *
   * The interesting unit in the roster: the same chassis is a scout, a
   * long-range sniper or a repair vehicle depending on who is inside it, so
   * the player composes it rather than picking it off a list.
   */
  readonly carriesInfantry?: boolean
  /**
   * Closest range this unit can fire at, in Q16.16 cells. Absent means none.
   *
   * A minimum range is what separates artillery from a longer-ranged tank. A
   * siege weapon that also defends itself is strictly better than the tank it
   * replaces; one that cannot shoot what is standing next to it has to be
   * escorted, which is a decision rather than an upgrade.
   */
  readonly minRange?: fix.Fixed

  /**
   * Flies. Ignores terrain and occupancy entirely: no flow field, no cell
   * reservation, nothing blocks it and it blocks nothing.
   */
  readonly isAir?: boolean
  /**
   * Shots before the aircraft must land and rearm. Red Alert's helicopters fly
   * one attack run and go home, and that single rule is what keeps a fast unit
   * that ignores the map from also being the only unit worth building: its
   * damage is bounded per sortie, and the trip home is a window to punish.
   */
  readonly ordnance?: number
  /** Ticks parked at an airbase to refill `ordnance`. */
  readonly rearmTime?: number
  /** Structure this unit is produced at and rearms over, if not the tab producer. */
  readonly homeStructure?: number
}

// Ids: 1xx Helios structures, 1xx+50 Helios units, 2xx Ascendancy mirrors.
export const ID = {
  // Helios structures
  CommandYard: 101,
  SolarArray: 102,
  RegolithRefinery: 103,
  HabitatBlock: 104,
  Foundry: 105,
  CommsMast: 106,
  GuardianTurret: 107,
  OrbitalMirrorLance: 108,
  LandingField: 109,
  PrismWorks: 110,
  // Helios units
  Mcv: 151,
  RegolithHarvester: 152,
  RifleSquad: 153,
  Engineer: 154,
  ScoutBuggy: 155,
  GuardianTank: 156,
  HornetGunship: 157,
  PrismTank: 158,
  SiegeBattery: 159,
  Warden: 160,
  Marksman: 161,
  Outrider: 162,

  // Ascendancy structures
  CoreNexus: 201,
  FusionCore: 202,
  NaniteRefinery: 203,
  ReplicationVat: 204,
  AssemblyHive: 205,
  SignalSpire: 206,
  ArcPylon: 207,
  LogicBomb: 208,
  LaunchSpire: 209,
  ResonanceVault: 210,
  // Ascendancy units
  MobileNexusFrame: 251,
  NaniteHarvester: 252,
  DroneSwarm: 253,
  Infiltrator: 254,
  Skirmisher: 255,
  SiegeWalker: 256,
  WraithSkimmer: 257,
  ArcLancer: 258,
  VoidMortar: 259,
  Colossus: 260,
  Nullifier: 261,
  Vector: 262,
} as const

const SECOND = 30

/** Every structure and unit, in one flat list. */
export const ENTITY_DEFS: EntityDef[] = [
  // =========================================================================
  // Helios Directorate — structures
  // =========================================================================
  {
    id: ID.CommandYard,
    key: 'command-yard',
    name: 'Command Yard',
    faction: FACTION.Helios,
    role: ROLE.BaseRoot,
    tab: TAB.Structure,
    isStructure: true,
    cost: 2000,
    buildTime: 40 * SECOND,
    prereqs: [],
    power: 25,
    hp: 1500,
    armor: ARMOR.Structure,
    weapon: NO_WEAPON,
    speed: fix.ZERO,
    vision: 8,
    width: 3,
    height: 3,
    producesTab: TAB.Structure,
  },
  {
    id: ID.SolarArray,
    key: 'solar-array',
    name: 'Solar Array',
    faction: FACTION.Helios,
    role: ROLE.Power,
    tab: TAB.Structure,
    isStructure: true,
    cost: 700,
    buildTime: 12 * SECOND,
    prereqs: [ID.CommandYard],
    power: 150,
    hp: 600,
    armor: ARMOR.Structure,
    weapon: NO_WEAPON,
    speed: fix.ZERO,
    vision: 5,
    width: 2,
    height: 2,
  },
  {
    id: ID.RegolithRefinery,
    key: 'regolith-refinery',
    name: 'Regolith Refinery',
    faction: FACTION.Helios,
    role: ROLE.Refinery,
    tab: TAB.Structure,
    isStructure: true,
    cost: 1800,
    buildTime: 26 * SECOND,
    prereqs: [ID.SolarArray],
    power: -40,
    hp: 1000,
    armor: ARMOR.Structure,
    weapon: NO_WEAPON,
    speed: fix.ZERO,
    vision: 6,
    width: 3,
    height: 2,
    freeUnit: ID.RegolithHarvester,
  },
  {
    id: ID.HabitatBlock,
    key: 'habitat-block',
    name: 'Habitat Block',
    faction: FACTION.Helios,
    role: ROLE.InfantryProduction,
    tab: TAB.Structure,
    isStructure: true,
    cost: 1800,
    buildTime: 18 * SECOND,
    prereqs: [ID.RegolithRefinery],
    power: -25,
    hp: 800,
    armor: ARMOR.Structure,
    weapon: NO_WEAPON,
    speed: fix.ZERO,
    vision: 5,
    width: 2,
    height: 2,
    producesTab: TAB.Infantry,
  },
  {
    id: ID.Foundry,
    key: 'foundry',
    name: 'Foundry',
    faction: FACTION.Helios,
    role: ROLE.VehicleProduction,
    tab: TAB.Structure,
    isStructure: true,
    cost: 2200,
    buildTime: 30 * SECOND,
    prereqs: [ID.RegolithRefinery],
    power: -50,
    hp: 1100,
    armor: ARMOR.Structure,
    weapon: NO_WEAPON,
    speed: fix.ZERO,
    vision: 5,
    width: 3,
    height: 3,
    producesTab: TAB.Vehicle,
  },
  {
    id: ID.CommsMast,
    key: 'comms-mast',
    name: 'Comms Mast',
    faction: FACTION.Helios,
    role: ROLE.TechLab,
    tab: TAB.Structure,
    isStructure: true,
    cost: 2400,
    buildTime: 34 * SECOND,
    prereqs: [ID.Foundry],
    power: -60,
    hp: 700,
    armor: ARMOR.Structure,
    weapon: NO_WEAPON,
    speed: fix.ZERO,
    vision: 12,
    width: 2,
    height: 2,
    // Deliberately does *not* survey the map. The Signal Spire does.
    //
    // This looked like an oversight — the two buildings are counterparts in
    // every other respect, same cost, same role, same prerequisite — so it was
    // given the flag for symmetry. Four sweeps of 180 matches each say the
    // asymmetry was load-bearing: with it, Helios wins 63-72% of non-mirror
    // matches; without it, 52%. Knowing where the enemy is turns out to be
    // worth more than any single unit in the roster, and the Ascendancy's other
    // numbers are tuned around being the side that has it.
    //
    // Restoring parity here means compensating the Ascendancy elsewhere, which
    // is a balance pass rather than a one-line change.
  },
  {
    // Late, expensive, and behind the tech lab on purpose. Air arrives when the
    // ground war is already decided one way or the other, and it decides it
    // faster — a unit that ignores the map should not be available while the
    // map is still being fought over.
    id: ID.LandingField,
    key: 'landing-field',
    name: 'Landing Field',
    faction: FACTION.Helios,
    role: ROLE.Airbase,
    tab: TAB.Structure,
    isStructure: true,
    cost: 2600,
    buildTime: 30 * SECOND,
    prereqs: [ID.CommsMast],
    power: -50,
    hp: 800,
    armor: ARMOR.Structure,
    weapon: NO_WEAPON,
    speed: fix.ZERO,
    vision: 7,
    width: 3,
    height: 3,
    producesTab: TAB.Vehicle,
  },
  {
    id: ID.GuardianTurret,
    key: 'guardian-turret',
    name: 'Guardian Turret',
    faction: FACTION.Helios,
    role: ROLE.Defense,
    tab: TAB.Defense,
    isStructure: true,
    cost: 900,
    buildTime: 12 * SECOND,
    prereqs: [ID.SolarArray],
    power: -25,
    hp: 700,
    armor: ARMOR.Structure,
    weapon: 4,
    speed: fix.ZERO,
    vision: 7,
    width: 1,
    height: 1,
  },
  {
    id: ID.OrbitalMirrorLance,
    key: 'orbital-mirror-lance',
    name: 'Orbital Mirror Lance',
    faction: FACTION.Helios,
    role: ROLE.Superweapon,
    tab: TAB.Structure,
    isStructure: true,
    cost: 5000,
    buildTime: 60 * SECOND,
    prereqs: [ID.CommsMast],
    power: -100,
    hp: 900,
    armor: ARMOR.Structure,
    weapon: 5,
    speed: fix.ZERO,
    vision: 6,
    width: 2,
    height: 2,
    chargeTime: 240 * SECOND,
  },

  // =========================================================================
  // Helios Directorate — units
  // =========================================================================
  {
    id: ID.Mcv,
    key: 'mcv',
    name: 'Mobile Command Vehicle',
    faction: FACTION.Helios,
    role: ROLE.Founder,
    tab: TAB.Vehicle,
    isStructure: false,
    cost: 2500,
    buildTime: 40 * SECOND,
    prereqs: [ID.Foundry, ID.CommsMast],
    power: 0,
    hp: 900,
    armor: ARMOR.Heavy,
    weapon: NO_WEAPON,
    speed: fix.fromFloat(0.035),
    vision: 7,
    width: 1,
    height: 1,
    founderOf: ID.CommandYard,
  },
  {
    id: ID.RegolithHarvester,
    key: 'regolith-harvester',
    name: 'Regolith Harvester',
    faction: FACTION.Helios,
    role: ROLE.Harvester,
    tab: TAB.Vehicle,
    isStructure: false,
    cost: 1200,
    buildTime: 20 * SECOND,
    prereqs: [ID.RegolithRefinery],
    power: 0,
    hp: 700,
    armor: ARMOR.Light,
    weapon: NO_WEAPON,
    speed: fix.fromFloat(0.05),
    vision: 5,
    width: 1,
    height: 1,
    isHarvester: true,
    capacity: 700,
  },
  {
    id: ID.RifleSquad,
    key: 'rifle-squad',
    name: 'Rifle Squad',
    faction: FACTION.Helios,
    role: ROLE.AntiInfantry,
    tab: TAB.Infantry,
    isStructure: false,
    cost: 200,
    buildTime: 6 * SECOND,
    prereqs: [ID.HabitatBlock],
    power: 0,
    hp: 120,
    armor: ARMOR.Infantry,
    weapon: 1,
    speed: fix.fromFloat(0.032),
    vision: 5,
    width: 1,
    height: 1,
    canDigIn: true,
  },
  {
    id: ID.Engineer,
    key: 'engineer',
    name: 'Engineer',
    faction: FACTION.Helios,
    role: ROLE.Support,
    tab: TAB.Infantry,
    isStructure: false,
    isEngineer: true,
    cost: 500,
    buildTime: 10 * SECOND,
    prereqs: [ID.HabitatBlock],
    power: 0,
    hp: 100,
    armor: ARMOR.Infantry,
    weapon: 21,
    speed: fix.fromFloat(0.03),
    vision: 5,
    width: 1,
    height: 1,
  },
  {
    id: ID.ScoutBuggy,
    key: 'scout-buggy',
    name: 'Scout Buggy',
    faction: FACTION.Helios,
    role: ROLE.Scout,
    tab: TAB.Vehicle,
    isStructure: false,
    cost: 500,
    buildTime: 9 * SECOND,
    prereqs: [ID.Foundry],
    power: 0,
    hp: 260,
    armor: ARMOR.Light,
    weapon: 2,
    speed: fix.fromFloat(0.095),
    vision: 9,
    width: 1,
    height: 1,
  },
  {
    id: ID.GuardianTank,
    key: 'guardian-tank',
    name: 'Guardian Tank',
    faction: FACTION.Helios,
    role: ROLE.MainBattle,
    tab: TAB.Vehicle,
    isStructure: false,
    cost: 1000,
    buildTime: 16 * SECOND,
    prereqs: [ID.Foundry],
    power: 0,
    hp: 620,
    armor: ARMOR.Heavy,
    weapon: 3,
    speed: fix.fromFloat(0.048),
    vision: 6,
    width: 1,
    height: 1,
  },

  {
    id: ID.HornetGunship,
    key: 'hornet-gunship',
    name: 'Hornet Gunship',
    faction: FACTION.Helios,
    role: ROLE.Air,
    tab: TAB.Vehicle,
    isStructure: false,
    // Three times a tank's speed, twice its price, three rounds per sortie.
    cost: 2000,
    buildTime: 24 * SECOND,
    prereqs: [ID.LandingField],
    power: 0,
    hp: 300,
    armor: ARMOR.Light,
    weapon: 6,
    speed: fix.fromFloat(0.14),
    vision: 9,
    width: 1,
    height: 1,
    isAir: true,
    ordnance: 3,
    rearmTime: 14 * SECOND,
    homeStructure: ID.LandingField,
  },

  // --- Helios: tier three ----------------------------------------------------
  {
    // The gate. Expensive, power-hungry and behind the tech lab, so a third
    // tier is a commitment made instead of an army rather than alongside one.
    id: ID.PrismWorks,
    key: 'prism-works',
    name: 'Prism Works',
    faction: FACTION.Helios,
    role: ROLE.AdvancedLab,
    tab: TAB.Structure,
    isStructure: true,
    cost: 2800,
    buildTime: 40 * SECOND,
    prereqs: [ID.CommsMast],
    power: -90,
    hp: 820,
    armor: ARMOR.Structure,
    weapon: NO_WEAPON,
    speed: fix.ZERO,
    vision: 8,
    width: 3,
    height: 3,
  },
  {
    // The reason to build the Works.
    //
    // Alone it is an expensive Guardian Tank with a longer reach. In numbers it
    // is something else: each Prism Tank near the one that fires feeds its beam
    // into the shot, so three together hit far harder than three separate
    // tanks. That inverts the usual rule — splash punishes clumping, and this
    // rewards it — which is the whole strategic point of the unit.
    id: ID.PrismTank,
    key: 'prism-tank',
    name: 'Prism Tank',
    faction: FACTION.Helios,
    role: ROLE.Beam,
    tab: TAB.Vehicle,
    isStructure: false,
    cost: 1600,
    buildTime: 26 * SECOND,
    prereqs: [ID.PrismWorks],
    power: 0,
    hp: 420,
    armor: ARMOR.Light,
    weapon: 7,
    speed: fix.fromFloat(0.036),
    vision: 8,
    width: 1,
    height: 1,
  },
  {
    // Outranges every static defence in the game. It cannot fire inside its own
    // minimum range, so it is a weapon you position rather than one you drive.
    id: ID.SiegeBattery,
    key: 'siege-battery',
    name: 'Siege Battery',
    faction: FACTION.Helios,
    role: ROLE.Artillery,
    tab: TAB.Vehicle,
    isStructure: false,
    cost: 1800,
    buildTime: 30 * SECOND,
    prereqs: [ID.PrismWorks],
    power: 0,
    hp: 340,
    armor: ARMOR.Light,
    weapon: 8,
    speed: fix.fromFloat(0.03),
    vision: 7,
    width: 1,
    height: 1,
    minRange: fix.fromFloat(3.5),
  },
  {
    // Slow, armoured, and the only Helios unit that wants to be shot at.
    id: ID.Warden,
    key: 'warden',
    name: 'Warden',
    faction: FACTION.Helios,
    role: ROLE.Heavy,
    tab: TAB.Vehicle,
    isStructure: false,
    cost: 2200,
    buildTime: 34 * SECOND,
    prereqs: [ID.PrismWorks],
    power: 0,
    hp: 1250,
    armor: ARMOR.Heavy,
    weapon: 9,
    speed: fix.fromFloat(0.028),
    vision: 6,
    width: 1,
    height: 1,
  },
  {
    // Outranges every unit in the game and cannot hurt a vehicle. It exists to
    // punish a player who answers armour with a crowd of infantry, and it digs
    // in — which is the pairing that makes it worth its cost, because standing
    // still is what a weapon with this reach wants to do anyway.
    id: ID.Marksman,
    key: 'marksman',
    name: 'Marksman',
    faction: FACTION.Helios,
    role: ROLE.Marksman,
    tab: TAB.Infantry,
    isStructure: false,
    cost: 700,
    buildTime: 13 * SECOND,
    prereqs: [ID.CommsMast],
    power: 0,
    hp: 90,
    armor: ARMOR.Infantry,
    weapon: 20,
    speed: fix.fromFloat(0.026),
    vision: 10,
    width: 1,
    height: 1,
    canDigIn: true,
  },
  {
    // Fast, thin, and only as dangerous as whoever is riding in it. Empty it
    // is a scout with a popgun; with a marksman inside it is a sniper that can
    // reposition, and with an engineer it keeps an army in the field.
    id: ID.Outrider,
    key: 'outrider',
    name: 'Outrider',
    faction: FACTION.Helios,
    role: ROLE.Transport,
    tab: TAB.Vehicle,
    isStructure: false,
    cost: 650,
    buildTime: 11 * SECOND,
    prereqs: [ID.Foundry],
    power: 0,
    hp: 260,
    armor: ARMOR.Light,
    weapon: 22,
    speed: fix.fromFloat(0.078),
    vision: 7,
    width: 1,
    height: 1,
    carriesInfantry: true,
  },

  // =========================================================================
  // The Ascendancy — structures
  // =========================================================================
  {
    id: ID.CoreNexus,
    key: 'core-nexus',
    name: 'Core Nexus',
    faction: FACTION.Ascendancy,
    role: ROLE.BaseRoot,
    tab: TAB.Structure,
    isStructure: true,
    cost: 2000,
    buildTime: 36 * SECOND,
    prereqs: [],
    power: 25,
    hp: 1400,
    armor: ARMOR.Structure,
    weapon: NO_WEAPON,
    speed: fix.ZERO,
    vision: 9,
    width: 3,
    height: 3,
    producesTab: TAB.Structure,
  },
  {
    id: ID.FusionCore,
    key: 'fusion-core',
    name: 'Fusion Core',
    faction: FACTION.Ascendancy,
    role: ROLE.Power,
    tab: TAB.Structure,
    isStructure: true,
    // Cheaper *and* more powerful than the Solar Array, against a much frailer
    // hull. The first balance sweep had the Ascendancy strictly worse at
    // everything — frailer buildings, a costlier main unit — and it won 25% of
    // matches. Speed alone does not pay for that; a real economic edge does.
    //
    // Power is the most compounding advantage in the game and this is the
    // single most sensitive number in the balance sweep. Measured over 118
    // non-mirror matches: at 700 the Ascendancy wins 60%, at 800 it wins 30%,
    // at 725 the match is even. Do not move it without re-running the sweep.
    cost: 725,
    buildTime: 14 * SECOND,
    prereqs: [ID.CoreNexus],
    // More power per building than the Solar Array, but frailer: the
    // Ascendancy's power grid is a better target.
    power: 190,
    hp: 460,
    armor: ARMOR.Structure,
    weapon: NO_WEAPON,
    speed: fix.ZERO,
    vision: 5,
    width: 2,
    height: 2,
  },
  {
    id: ID.NaniteRefinery,
    key: 'nanite-refinery',
    name: 'Nanite Refinery',
    faction: FACTION.Ascendancy,
    role: ROLE.Refinery,
    tab: TAB.Structure,
    isStructure: true,
    cost: 1800,
    buildTime: 23 * SECOND,
    prereqs: [ID.FusionCore],
    power: -45,
    hp: 950,
    armor: ARMOR.Structure,
    weapon: NO_WEAPON,
    speed: fix.ZERO,
    vision: 6,
    width: 3,
    height: 2,
    freeUnit: ID.NaniteHarvester,
  },
  {
    id: ID.ReplicationVat,
    key: 'replication-vat',
    name: 'Replication Vat',
    faction: FACTION.Ascendancy,
    role: ROLE.InfantryProduction,
    tab: TAB.Structure,
    isStructure: true,
    cost: 1800,
    buildTime: 16 * SECOND,
    prereqs: [ID.NaniteRefinery],
    power: -30,
    hp: 760,
    armor: ARMOR.Structure,
    weapon: NO_WEAPON,
    speed: fix.ZERO,
    vision: 5,
    width: 2,
    height: 2,
    producesTab: TAB.Infantry,
  },
  {
    id: ID.AssemblyHive,
    key: 'assembly-hive',
    name: 'Assembly Hive',
    faction: FACTION.Ascendancy,
    role: ROLE.VehicleProduction,
    tab: TAB.Structure,
    isStructure: true,
    cost: 2200,
    buildTime: 29 * SECOND,
    prereqs: [ID.NaniteRefinery],
    power: -55,
    hp: 1050,
    armor: ARMOR.Structure,
    weapon: NO_WEAPON,
    speed: fix.ZERO,
    vision: 5,
    width: 3,
    height: 3,
    producesTab: TAB.Vehicle,
  },
  {
    id: ID.SignalSpire,
    key: 'signal-spire',
    name: 'Signal Spire',
    faction: FACTION.Ascendancy,
    role: ROLE.TechLab,
    tab: TAB.Structure,
    isStructure: true,
    cost: 2400,
    buildTime: 33 * SECOND,
    prereqs: [ID.AssemblyHive],
    power: -65,
    hp: 640,
    armor: ARMOR.Structure,
    weapon: NO_WEAPON,
    speed: fix.ZERO,
    vision: 14,
    width: 2,
    height: 2,
    revealsMap: true,
  },
  {
    id: ID.LaunchSpire,
    key: 'launch-spire',
    name: 'Launch Spire',
    faction: FACTION.Ascendancy,
    role: ROLE.Airbase,
    tab: TAB.Structure,
    isStructure: true,
    cost: 2600,
    buildTime: 30 * SECOND,
    prereqs: [ID.SignalSpire],
    power: -50,
    hp: 760,
    armor: ARMOR.Structure,
    weapon: NO_WEAPON,
    speed: fix.ZERO,
    vision: 7,
    width: 3,
    height: 3,
    producesTab: TAB.Vehicle,
  },
  {
    id: ID.ArcPylon,
    key: 'arc-pylon',
    name: 'Arc Pylon',
    faction: FACTION.Ascendancy,
    role: ROLE.Defense,
    tab: TAB.Defense,
    isStructure: true,
    cost: 850,
    buildTime: 11 * SECOND,
    prereqs: [ID.FusionCore],
    power: -30,
    hp: 620,
    armor: ARMOR.Structure,
    weapon: 14,
    speed: fix.ZERO,
    vision: 7,
    width: 1,
    height: 1,
  },
  {
    id: ID.LogicBomb,
    key: 'logic-bomb',
    name: 'Logic Bomb',
    faction: FACTION.Ascendancy,
    role: ROLE.Superweapon,
    tab: TAB.Structure,
    isStructure: true,
    cost: 5000,
    buildTime: 60 * SECOND,
    prereqs: [ID.SignalSpire],
    power: -110,
    hp: 850,
    armor: ARMOR.Structure,
    weapon: 15,
    speed: fix.ZERO,
    vision: 6,
    width: 2,
    height: 2,
    // Slightly faster to charge than the Mirror Lance, and slightly weaker —
    // the Ascendancy trades peak damage for tempo across the whole roster.
    chargeTime: 225 * SECOND,
  },

  // =========================================================================
  // The Ascendancy — units
  // =========================================================================
  {
    id: ID.MobileNexusFrame,
    key: 'mobile-nexus-frame',
    name: 'Mobile Nexus Frame',
    faction: FACTION.Ascendancy,
    role: ROLE.Founder,
    tab: TAB.Vehicle,
    isStructure: false,
    cost: 2500,
    buildTime: 40 * SECOND,
    prereqs: [ID.AssemblyHive, ID.SignalSpire],
    power: 0,
    hp: 820,
    armor: ARMOR.Heavy,
    weapon: NO_WEAPON,
    speed: fix.fromFloat(0.04),
    vision: 7,
    width: 1,
    height: 1,
    founderOf: ID.CoreNexus,
  },
  {
    id: ID.NaniteHarvester,
    key: 'nanite-harvester',
    name: 'Nanite Harvester',
    faction: FACTION.Ascendancy,
    role: ROLE.Harvester,
    tab: TAB.Vehicle,
    isStructure: false,
    cost: 1200,
    buildTime: 20 * SECOND,
    prereqs: [ID.NaniteRefinery],
    power: 0,
    hp: 620,
    armor: ARMOR.Light,
    weapon: NO_WEAPON,
    // Faster but carries less: more trips, more exposure, same throughput.
    speed: fix.fromFloat(0.062),
    vision: 5,
    width: 1,
    height: 1,
    isHarvester: true,
    capacity: 560,
  },
  {
    id: ID.DroneSwarm,
    key: 'drone-swarm',
    name: 'Drone Swarm',
    faction: FACTION.Ascendancy,
    role: ROLE.AntiInfantry,
    tab: TAB.Infantry,
    isStructure: false,
    cost: 180,
    buildTime: 5 * SECOND,
    prereqs: [ID.ReplicationVat],
    power: 0,
    hp: 95,
    armor: ARMOR.Infantry,
    weapon: 11,
    speed: fix.fromFloat(0.042),
    vision: 5,
    width: 1,
    height: 1,
    canDigIn: true,
  },
  {
    id: ID.Infiltrator,
    key: 'infiltrator',
    name: 'Infiltrator',
    faction: FACTION.Ascendancy,
    role: ROLE.Support,
    tab: TAB.Infantry,
    isStructure: false,
    isEngineer: true,
    cost: 500,
    buildTime: 10 * SECOND,
    prereqs: [ID.ReplicationVat],
    power: 0,
    hp: 90,
    armor: ARMOR.Infantry,
    weapon: 21,
    speed: fix.fromFloat(0.038),
    vision: 6,
    width: 1,
    height: 1,
  },
  {
    id: ID.Skirmisher,
    key: 'skirmisher',
    name: 'Skirmisher',
    faction: FACTION.Ascendancy,
    role: ROLE.Scout,
    tab: TAB.Vehicle,
    isStructure: false,
    cost: 500,
    buildTime: 9 * SECOND,
    prereqs: [ID.AssemblyHive],
    power: 0,
    hp: 230,
    armor: ARMOR.Light,
    weapon: 12,
    speed: fix.fromFloat(0.088),
    vision: 9,
    width: 1,
    height: 1,
  },
  {
    id: ID.SiegeWalker,
    key: 'siege-walker',
    name: 'Siege Walker',
    faction: FACTION.Ascendancy,
    role: ROLE.MainBattle,
    tab: TAB.Vehicle,
    isStructure: false,
    // Priced level with the Guardian Tank, measured over 119 non-mirror
    // matches. At 1100 it lost on every axis at once (Ascendancy 25%); at 950,
    // stacked with cheaper power and faster construction, it swung too far the
    // other way (Ascendancy 62%). Its splash against a clumped enemy is worth
    // roughly the tank's extra armour and shorter cooldown.
    cost: 1000,
    buildTime: 18 * SECOND,
    prereqs: [ID.AssemblyHive],
    power: 0,
    hp: 580,
    armor: ARMOR.Heavy,
    weapon: 13,
    speed: fix.fromFloat(0.04),
    vision: 7,
    width: 1,
    height: 1,
  },
  {
    id: ID.WraithSkimmer,
    key: 'wraith-skimmer',
    name: 'Wraith Skimmer',
    faction: FACTION.Ascendancy,
    role: ROLE.Air,
    tab: TAB.Vehicle,
    isStructure: false,
    cost: 2000,
    buildTime: 24 * SECOND,
    prereqs: [ID.LaunchSpire],
    power: 0,
    hp: 280,
    armor: ARMOR.Light,
    // A lighter round than the Hornet's, and the same three of them: the
    // Ascendancy trades payload for a longer reach and a faster airframe.
    weapon: 16,
    speed: fix.fromFloat(0.15),
    vision: 9,
    width: 1,
    height: 1,
    isAir: true,
    ordnance: 3,
    rearmTime: 15 * SECOND,
    homeStructure: ID.LaunchSpire,
  },

  // --- the Ascendancy: tier three -------------------------------------------
  {
    id: ID.ResonanceVault,
    key: 'resonance-vault',
    name: 'Resonance Vault',
    faction: FACTION.Ascendancy,
    role: ROLE.AdvancedLab,
    tab: TAB.Structure,
    isStructure: true,
    cost: 2800,
    buildTime: 40 * SECOND,
    prereqs: [ID.SignalSpire],
    power: -95,
    hp: 780,
    armor: ARMOR.Structure,
    weapon: NO_WEAPON,
    speed: fix.ZERO,
    vision: 8,
    width: 3,
    height: 3,
  },
  {
    // The same tier and the same counters as the Prism Tank, and a different
    // reason to position it: the Lance Arc does not chain, it *pierces*. One
    // shot damages everything standing between the Lancer and its target.
    //
    // Two factions with the same answer to the same problem is two factions
    // that play identically. Chaining rewards massing your own units; piercing
    // rewards catching theirs in a line. Neither is stronger; they ask
    // different questions of the person on the other side.
    id: ID.ArcLancer,
    key: 'arc-lancer',
    name: 'Arc Lancer',
    faction: FACTION.Ascendancy,
    role: ROLE.Beam,
    tab: TAB.Vehicle,
    isStructure: false,
    cost: 1550,
    buildTime: 25 * SECOND,
    prereqs: [ID.ResonanceVault],
    power: 0,
    hp: 400,
    armor: ARMOR.Light,
    weapon: 17,
    speed: fix.fromFloat(0.034),
    vision: 8,
    width: 1,
    height: 1,
  },
  {
    id: ID.VoidMortar,
    key: 'void-mortar',
    name: 'Void Mortar',
    faction: FACTION.Ascendancy,
    role: ROLE.Artillery,
    tab: TAB.Vehicle,
    isStructure: false,
    cost: 1750,
    buildTime: 30 * SECOND,
    prereqs: [ID.ResonanceVault],
    power: 0,
    hp: 320,
    armor: ARMOR.Light,
    weapon: 18,
    speed: fix.fromFloat(0.032),
    vision: 7,
    width: 1,
    height: 1,
    minRange: fix.fromFloat(3.5),
  },
  {
    // The Ascendancy's brawler is an area weapon rather than a gun: shorter
    // ranged than the Warden and far better against the light armour and
    // infantry a Warden struggles with. It has to walk in; what it reaches, it
    // ruins.
    id: ID.Colossus,
    key: 'colossus',
    name: 'Colossus',
    faction: FACTION.Ascendancy,
    role: ROLE.Heavy,
    tab: TAB.Vehicle,
    isStructure: false,
    cost: 2300,
    buildTime: 35 * SECOND,
    prereqs: [ID.ResonanceVault],
    power: 0,
    hp: 1180,
    armor: ARMOR.Heavy,
    weapon: 19,
    speed: fix.fromFloat(0.026),
    vision: 6,
    width: 1,
    height: 1,
  },
  {
    // The Ascendancy's marksman. Same numbers as the Helios one, because a
    // mirrored counter that is not actually mirrored is a faction advantage
    // under another name.
    id: ID.Nullifier,
    key: 'nullifier',
    name: 'Nullifier',
    faction: FACTION.Ascendancy,
    role: ROLE.Marksman,
    tab: TAB.Infantry,
    isStructure: false,
    cost: 700,
    buildTime: 13 * SECOND,
    prereqs: [ID.SignalSpire],
    power: 0,
    hp: 90,
    armor: ARMOR.Infantry,
    weapon: 20,
    speed: fix.fromFloat(0.026),
    vision: 10,
    width: 1,
    height: 1,
    canDigIn: true,
  },
  {
    id: ID.Vector,
    key: 'vector',
    name: 'Vector',
    faction: FACTION.Ascendancy,
    role: ROLE.Transport,
    tab: TAB.Vehicle,
    isStructure: false,
    cost: 650,
    buildTime: 11 * SECOND,
    prereqs: [ID.AssemblyHive],
    power: 0,
    hp: 260,
    armor: ARMOR.Light,
    weapon: 22,
    speed: fix.fromFloat(0.078),
    vision: 7,
    width: 1,
    height: 1,
    carriesInfantry: true,
  },
]

// ---------------------------------------------------------------------------
// Lookups
// ---------------------------------------------------------------------------

const defsById = new Map<number, EntityDef>()
const defsByKey = new Map<string, EntityDef>()
for (const d of ENTITY_DEFS) {
  defsById.set(d.id, d)
  defsByKey.set(d.key, d)
}

export function defById(id: number): EntityDef | undefined {
  return defsById.get(id)
}

export function defByKey(key: string): EntityDef | undefined {
  return defsByKey.get(key)
}

export function defsForFaction(faction: number): EntityDef[] {
  return ENTITY_DEFS.filter((d) => d.faction === faction)
}

export function structuresFor(faction: number): EntityDef[] {
  return defsForFaction(faction).filter((d) => d.isStructure)
}

export function unitsFor(faction: number): EntityDef[] {
  return defsForFaction(faction).filter((d) => !d.isStructure)
}

/** The structure a faction's base grows from — what its founder deploys into. */
export function rootStructure(faction: number): EntityDef {
  const root = defsForFaction(faction).find((d) => d.role === ROLE.BaseRoot)
  if (!root) throw new Error(`faction ${faction} has no base-root structure`)
  return root
}

/** The founder unit a match starts each player with. */
export function founderUnit(faction: number): EntityDef {
  const founder = defsForFaction(faction).find((d) => d.role === ROLE.Founder)
  if (!founder) throw new Error(`faction ${faction} has no founder unit`)
  return founder
}
